import React from "react";
import { Button, Form, Input, Modal, Popconfirm, Select, Space, Tabs, Tag } from "antd";
import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import AppLayout from "../components/Layout";
import DataTable from "../components/DataTable";
import SearchBox from "../components/SearchBox";
import usePagedList from "../lib/usePagedList";
import httpClient from "../axios";
import { notifyError, notifySuccess } from "../helper";
import { isSuperAdmin, ROLE_ADMIN, ROLE_SUPER_ADMIN } from "../lib/auth";

const ROLE_TABS = [
	{ key: ROLE_ADMIN, label: "Admins" },
	{ key: ROLE_SUPER_ADMIN, label: "Super admins" },
	{ key: "player", label: "App users" },
];

const roleColor = (role) => (role === ROLE_SUPER_ADMIN ? "purple" : role === ROLE_ADMIN ? "cyan" : "default");

/** Admin-panel accounts (and fire-extinguisher app users). */
const AdminUsers = () => {
	const http = React.useMemo(() => httpClient(), []);
	const [role, setRole] = React.useState(ROLE_ADMIN);
	const list = usePagedList("/users", { params: { role } });
	const [editing, setEditing] = React.useState(null); // null | {} (new) | user
	const [saving, setSaving] = React.useState(false);
	const [form] = Form.useForm();

	const assignableRoles = isSuperAdmin() ? [ROLE_ADMIN, ROLE_SUPER_ADMIN] : [ROLE_ADMIN];

	const openNew = () => {
		form.resetFields();
		form.setFieldsValue({ role: role === "player" ? ROLE_ADMIN : role });
		setEditing({});
	};

	const openEdit = (user) => {
		form.resetFields();
		form.setFieldsValue({
			first_name: user.first_name,
			last_name: user.last_name,
			email: user.email,
			role: user.role,
		});
		setEditing(user);
	};

	const save = (values) => {
		setSaving(true);
		const payload = {
			first_name: values.first_name.trim(),
			last_name: values.last_name.trim(),
			email: values.email.trim(),
			role: values.role,
		};
		if (values.password) {
			payload.password = values.password;
			payload.password_confirmation = values.password_confirmation;
		}
		const request = editing.id ? http.put(`/users/${editing.id}`, payload) : http.post("/auth/register", payload);
		request
			.then(() => {
				notifySuccess(editing.id ? "User updated" : "User created");
				setEditing(null);
				list.reload();
			})
			.catch((error) => notifyError(error, "Could not save the user"))
			.finally(() => setSaving(false));
	};

	const remove = (user) => {
		http
			.delete(`/users/${user.id}`)
			.then(() => {
				notifySuccess("User deleted");
				list.reload();
			})
			.catch((error) => notifyError(error, "Could not delete the user"));
	};

	const canManage = (user) => user.role !== ROLE_SUPER_ADMIN || isSuperAdmin();

	const columns = [
		{ title: "Name", key: "name", render: (u) => [u.first_name, u.last_name].filter(Boolean).join(" ") || u.username || "-" },
		{ title: "Email", dataIndex: "email", key: "email", render: (v) => v || "-" },
		{ title: "Username", dataIndex: "username", key: "username", render: (v) => v || "-" },
		{ title: "Role", dataIndex: "role", key: "role", render: (v) => (v ? <Tag color={roleColor(v)}>{v}</Tag> : "-") },
		{
			title: "",
			key: "actions",
			align: "right",
			render: (user) =>
				canManage(user) && user.role !== "player" ? (
					<Space>
						<Button size="small" icon={<EditOutlined />} onClick={() => openEdit(user)}>
							Edit
						</Button>
						<Popconfirm title={`Delete ${user.email}?`} okText="Delete" okButtonProps={{ danger: true }} onConfirm={() => remove(user)}>
							<Button size="small" danger icon={<DeleteOutlined />}>
								Delete
							</Button>
						</Popconfirm>
					</Space>
				) : null,
		},
	];

	return (
		<AppLayout
			title="Users"
			subtitle="Accounts that can sign in to this admin panel."
			actions={
				<>
					<SearchBox onSearch={list.onSearch} placeholder="Search name or email" />
					<Button type="primary" size="large" icon={<PlusOutlined />} onClick={openNew}>
						Add user
					</Button>
				</>
			}
		>
			<Tabs activeKey={role} onChange={setRole}>
				{ROLE_TABS.map((t) => (
					<Tabs.TabPane tab={t.label} key={t.key} />
				))}
			</Tabs>
			<DataTable rows={list.rows} columns={columns} loading={list.loading} pagination={list.pagination} />

			<Modal
				title={editing && editing.id ? "Edit user" : "Add user"}
				visible={Boolean(editing)}
				onCancel={() => setEditing(null)}
				footer={null}
				destroyOnClose={false}
			>
				<Form form={form} layout="vertical" onFinish={save} requiredMark={false} size="large">
					<div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
						<Form.Item label="First name" name="first_name" rules={[{ required: true, message: "Required" }]}>
							<Input />
						</Form.Item>
						<Form.Item label="Last name" name="last_name" rules={[{ required: true, message: "Required" }]}>
							<Input />
						</Form.Item>
					</div>
					<Form.Item label="Email" name="email" rules={[{ required: true, type: "email", message: "Enter a valid email" }]}>
						<Input />
					</Form.Item>
					<Form.Item label="Role" name="role" rules={[{ required: true, message: "Pick a role" }]}>
						<Select options={assignableRoles.map((r) => ({ value: r, label: r }))} />
					</Form.Item>
					<div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
						<Form.Item
							label={editing && editing.id ? "New password (optional)" : "Password"}
							name="password"
							rules={[{ required: !(editing && editing.id), min: 8, message: "At least 8 characters" }]}
						>
							<Input.Password autoComplete="new-password" />
						</Form.Item>
						<Form.Item
							label="Confirm password"
							name="password_confirmation"
							dependencies={["password"]}
							rules={[
								({ getFieldValue }) => ({
									validator(_, value) {
										const pwd = getFieldValue("password");
										if (!pwd && !value) return Promise.resolve();
										return pwd === value ? Promise.resolve() : Promise.reject(new Error("Passwords do not match"));
									},
								}),
							]}
						>
							<Input.Password autoComplete="new-password" />
						</Form.Item>
					</div>
					<Space>
						<Button type="primary" htmlType="submit" loading={saving}>
							{editing && editing.id ? "Save changes" : "Create user"}
						</Button>
						<Button onClick={() => setEditing(null)}>Cancel</Button>
					</Space>
				</Form>
			</Modal>
		</AppLayout>
	);
};

export default AdminUsers;
